import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { useT } from '../i18n/useT'

const API_URL = import.meta.env.VITE_API_URL || '/v1'

interface User {
  id: string
  username: string
  fullName: string
  /** Short handle shown in Flixpector once a document is handed over */
  initials: string | null
  email: string | null
  role: 'user' | 'admin'
  isActive: boolean
  lastLogin: string | null
}

interface CreateUserInput {
  username: string
  password: string
  fullName: string
  initials?: string | null
  email?: string
  role?: 'user' | 'admin'
}

interface UpdateUserInput {
  fullName?: string
  initials?: string | null
  email?: string
  role?: 'user' | 'admin'
  isActive?: boolean
  password?: string
}

export default function AdminPage() {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const { t } = useT()
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [formData, setFormData] = useState<CreateUserInput | UpdateUserInput>({
    username: '',
    password: '',
    fullName: '',
    email: '',
    role: 'user',
    isActive: true,
  })

  // Redirect if not admin
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin') {
      navigate('/')
    }
  }, [currentUser, navigate])

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users)
      } else {
        setError(t('Users could not be loaded'))
      }
    } catch (err) {
      setError(t('Users could not be loaded'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers()
    }
  }, [currentUser])

  const handleCreateUser = () => {
    setDialogMode('create')
    setFormData({
      username: '',
      password: '',
      fullName: '',
      initials: '',
      email: '',
      role: 'user',
    })
    setDialogOpen(true)
  }

  const handleEditUser = (user: User) => {
    setDialogMode('edit')
    setSelectedUser(user)
    setFormData({
      fullName: user.fullName,
      initials: user.initials ?? '',
      email: user.email || '',
      role: user.role,
      isActive: user.isActive,
    })
    setDialogOpen(true)
  }

  const handleDeleteUser = async (user: User) => {
    if (!confirm(t('Really delete the user "{name}"?').replace('{name}', user.username))) {
      return
    }

    try {
      const response = await fetch(`${API_URL}/admin/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (response.ok) {
        fetchUsers()
      } else {
        const data = await response.json()
        setError(data.error || t('User could not be deleted'))
      }
    } catch (err) {
      setError(t('User could not be deleted'))
    }
  }

  const handleToggleActive = async (user: User) => {
    try {
      const response = await fetch(`${API_URL}/admin/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !user.isActive }),
      })

      if (response.ok) {
        fetchUsers()
      } else {
        const data = await response.json()
        setError(data.error || t('User could not be saved'))
      }
    } catch (err) {
      setError(t('User could not be saved'))
    }
  }

  const handleDialogSubmit = async () => {
    setError(null)

    try {
      if (dialogMode === 'create') {
        const data = formData as CreateUserInput
        const response = await fetch(`${API_URL}/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        })

        if (response.ok) {
          setDialogOpen(false)
          fetchUsers()
        } else {
          const result = await response.json()
          setError(result.error || t('User could not be created'))
        }
      } else {
        const data = formData as UpdateUserInput
        const response = await fetch(`${API_URL}/admin/users/${selectedUser!.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        })

        if (response.ok) {
          setDialogOpen(false)
          fetchUsers()
        } else {
          const result = await response.json()
          setError(result.error || t('User could not be saved'))
        }
      }
    } catch (err) {
      setError(t('User could not be saved'))
    }
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {t('User management')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          {t('Add user')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Username')}</TableCell>
                  <TableCell>{t('Full name')}</TableCell>
                  <TableCell>{t('Initials')}</TableCell>
                  <TableCell>{t('Email')}</TableCell>
                  <TableCell>{t('Role')}</TableCell>
                  <TableCell>{t('Status')}</TableCell>
                  <TableCell>{t('Last login')}</TableCell>
                  <TableCell align="right">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell>{user.initials || '—'}</TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role === 'admin' ? t('Admin') : t('User')}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? t('Active') : t('Inactive')}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                        icon={user.isActive ? <CheckCircleIcon /> : <BlockIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : t('Never')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        disabled={user.id === currentUser?.id}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(user)}
                        disabled={user.id === currentUser?.id}
                        color={user.isActive ? 'warning' : 'success'}
                      >
                        {user.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user)}
                        disabled={user.id === currentUser?.id}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? t('Create new user') : t('Edit user')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {dialogMode === 'create' && (
              <TextField
                label={t('Username')}
                fullWidth
                value={(formData as CreateUserInput).username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            )}
            {dialogMode === 'create' && (
              <TextField
                label={t('Password')}
                type="password"
                fullWidth
                value={(formData as CreateUserInput).password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                helperText={t('At least 8 characters')}
              />
            )}
            <TextField
              label={t('Full name')}
              fullWidth
              value={formData.fullName || ''}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              required
            />
            <TextField
              label={t('Initials')}
              fullWidth
              value={formData.initials || ''}
              onChange={(e) => setFormData({ ...formData, initials: e.target.value })}
              inputProps={{ maxLength: 5 }}
              helperText={t(
                'Optional, e.g. LEW — shown in Flixpector on every line. Empty = derived from the name.',
              )}
            />
            <TextField
              label={t('Email')}
              type="email"
              fullWidth
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>{t('Role')}</InputLabel>
              <Select
                value={formData.role || 'user'}
                label={t('Role')}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'user' | 'admin' })}
              >
                <MenuItem value="user">{t('User')}</MenuItem>
                <MenuItem value="admin">{t('Admin')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
          <Button variant="contained" onClick={handleDialogSubmit}>
            {dialogMode === 'create' ? t('Create') : t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
